import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

/**
 * The signed-in user's organization logo, shown beside the platform mark in
 * the app chrome (sidebar, top nav, footer).
 *
 * Renders nothing for BusinessLocating users: `org_logo_url` is null for the
 * default organization, whose logo already IS the platform logo — so a single
 * truthiness check covers "no org", "default org" and "org without a logo".
 *
 * The logo keeps its own brand colours in both themes; a partner's mark is
 * never recoloured to fit our palette.
 */
export default function OrgCoBrand({
  className,
  dividerClassName,
  divider = "before",
}: {
  /** Sizing for the organization logo. */
  className?: string;
  /** Sizing for the vertical rule. */
  dividerClassName?: string;
  /** Which side the rule sits on — "before" when the logo trails the platform
   *  mark, "after" when it leads a group of controls. */
  divider?: "before" | "after" | "none";
}) {
  const { user } = useAuth();
  if (!user?.org_logo_url) return null;

  const rule =
    divider === "none" ? null : (
      <span className={cn("w-px shrink-0 bg-border/80", dividerClassName ?? "h-6")} aria-hidden="true" />
    );

  return (
    <>
      {divider === "before" && rule}
      <img
        src={user.org_logo_url}
        alt={user.org_name ?? "Organization"}
        className={cn("w-auto shrink-0 object-contain", className ?? "h-8 max-w-[9rem]")}
      />
      {divider === "after" && rule}
    </>
  );
}
